import axios from "./_axios";
import { API } from "./apiEndPoints";
import { refreshAccountCounts } from "@/utils/accountCountUtils";

function requestV2RoleList(account_id, params) {
  return axios()({
    method: "get",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/role/`,
    params,
  });
}

export const getListofResponsibility = (account_id, page_no, size) => {
  let result = axios()({
    method: "get",
    url: `${API.RESPONSIBILITY}${account_id}/responsibility/`,
    params: { page: page_no, size: size },
  });
  return result;
};
export const getListofResponsibilityForBadges = ({ accountId, limit, offset, name, status }) => {
  const params = {
    limit: limit,
    offset: offset,
    name: name,
    status: status,
    archived: false,
  };
  let result = axios()({
    method: "get",
    url: `${API.RESPONSIBILITY_V2}${accountId}/responsibility/`,
    params: params,
  });
  return result;
};
export const getListofResponsibilityV2 = (
  account_id,
  limit,
  offset,
  search,
  filters = {},
  ordering = null,
  include_badge_holders = false
) => {
  const params = {
    limit: limit,
    offset: offset,
    name: search,
    include_badge_holders: include_badge_holders,
  };

  // Add status filters (multiple values allowed)
  if (filters.status && filters.status.length > 0) {
    params.status = filters.status;
  }

  // Add coverage filters (single value)
  if (filters.no_masters) {
    params.no_masters = true;
  }
  if (filters.no_coaches) {
    params.no_coaches = true;
  }
  if (filters.no_professionals) {
    params.no_professionals = true;
  }

  // Add archived filter
  if (filters.archived !== undefined) {
    params.archived = filters.archived;
  }

  // Add role filter
  if (filters.role) {
    params.role = filters.role;
  }

  // Add attachments (roles) filter — "no" = no role attachments, "yes" = has role attachments
  if (filters.attachments === "no" || filters.attachments === "yes") {
    params.attachments = filters.attachments;
  }

  // Add roles filter (for "No Roles" - responsibilities not attached to any roles)
  if (filters.roles === "no") {
    params.roles = "no";
  }

  // Add checklist filter (no_checklist / draft_only / has_active)
  if (filters.checklist) {
    params.checklist = filters.checklist;
  }

  // Add in-use filter (true = has current assignments, false = not used)
  if (filters.in_use !== undefined) {
    params.in_use = filters.in_use;
  }

  // Add timeline filters
  if (filters.recently_activated) {
    params.recently_activated = true;
  }
  if (filters.recently_retired) {
    params.recently_retired = true;
  }
  if (filters.recently_deactivated) {
    params.recently_deactivated = true;
  }
  if (filters.recently_created) {
    params.recently_created = true;
  }

  // Add ordering
  if (ordering) {
    params.ordering = ordering;
  }

  let result = axios()({
    method: "get",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/`,
    params: params,
    paramsSerializer: (params) => {
      const parts = [];
      Object.keys(params).forEach((key) => {
        const value = params[key];
        if (Array.isArray(value)) {
          value.forEach((v) => {
            parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(v)}`);
          });
        } else if (value !== undefined) {
          parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
        }
      });
      return parts.join("&");
    },
  });
  return result;
};
export const getResponsibilityAttachments = (account_id, responsibility_id) =>
  requestV2RoleList(account_id, { responsibility: responsibility_id, limit: 50 });

export const createResponsibility = (account_id, data) => {
  let result = axios()({
    method: "post",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/`,
    data: data,
  });
  return result;
};

export const deleteResponsibility = (account_id, res_id) => {
  let result = axios()({
    method: "delete",
    url: `${API.RESPONSIBILITY}${account_id}/responsibility/${res_id}/`,
  });
  return result;
};
export const updateActivateResponsibility = async (account_id, responsibility_id, data) => {
  let result = await axios()({
    method: "post",
    url: `${API.ACCOUNTList}${account_id}/responsibility/${responsibility_id}/activate/`,
    data: data,
  });
  await refreshAccountCounts(account_id);
  return result;
};

// ER-11818 / Responsibility Canonical Rules Rule 3.4 — read-only preview of how many
// employees will be assigned when a DRAFT Responsibility is activated (N).
// Contract owned by backend ER-11817.
export const getResponsibilityActivatePreview = (account_id, responsibility_id) => {
  let result = axios()({
    method: "get",
    url: `${API.ACCOUNTList}${account_id}/responsibility/${responsibility_id}/activate/preview/`,
  });
  return result;
};
export const responsibilityStateUpdate = (account_id, res_id, data) => {
  let result = axios()({
    method: "put",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${res_id}/`,
    data: data,
  });
  return result;
};
export const updateNameResponsibility = (account_id, responsibility_id, data) => {
  let result = axios()({
    method: "post",
    url: `${API.ACCOUNTList}${account_id}/responsibility/${responsibility_id}/rename/`,
    data: data,
  });
  return result;
};
export const updateResponsibilityPurpose = (account_id, res_id, data) => {
  let result = axios()({
    method: "patch",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${res_id}/`,
    data: data,
  });
  return result;
};

// ER-9894: read/write responsibility.context (server stamps context_updated_at/by).
// Same PATCH viewset as the legacy purpose write; payload carries `context`.
export const updateResponsibilityContext = (account_id, res_id, data) => {
  let result = axios()({
    method: "patch",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${res_id}/`,
    data: data,
  });
  return result;
};

export const updateRetireResponsibility = async (account_id, res_id, data) => {
  let result = await axios()({
    method: "post",
    url: `${API.ACCOUNTList}${account_id}/responsibility/${res_id}/retire/`,
    data: data,
  });

  // Update account counts after retiring responsibility
  await refreshAccountCounts(account_id);

  return result;
};
export const updateReActivateResponsibility = async (account_id, res_id, data) => {
  let result = await axios()({
    method: "post",
    url: `${API.ACCOUNTList}${account_id}/responsibility/${res_id}/re-activate/`,
    data: data,
  });
  await refreshAccountCounts(account_id);
  return result;
};

// ER-11818 / Responsibility Canonical Rules Rule 3.6 — read-only preview of how many
// employees will be assigned when a RETIRED Responsibility is reactivated (N).
// Contract owned by backend ER-11817.
export const getResponsibilityReactivatePreview = (account_id, responsibility_id) => {
  let result = axios()({
    method: "get",
    url: `${API.ACCOUNTList}${account_id}/responsibility/${responsibility_id}/re-activate/preview/`,
  });
  return result;
};
export const updateArchiveResponsibility = (account_id, objType, orgId) => {
  let result = axios()({
    method: "post",
    url: `${API.ACCOUNTList}${account_id}/${objType}/${orgId}/archive/`,
  });
  return result;
};
export const updateUnArchiveResponsibility = (account_id, objType, orgId, name) => {
  let result = axios()({
    method: "post",
    url: `${API.ACCOUNTList}${account_id}/${objType}/${orgId}/unarchive/`,
    ...(name ? { data: { name } } : {}),
  });
  return result;
};

export const deleteRoleFromResponsibility = (account_id, res_id, role_id) => {
  let result = axios()({
    method: "delete",
    url: `${API.RESPONSIBILITY}${account_id}/responsibility/${res_id}/role-to-responsibility/${role_id}/`,
  });
  return result;
};

// ER-11177 / Rule 18 — convert a directly-attached ResponsibilityToPosition attachment
// into a role-sourced attachment on the given role. The backend detaches the direct
// attachment and creates the role-sourced assignments atomically.
// POST .../responsibility-to-position/{attachment_id}/convert-to-role/  { role_id }
export const convertResponsibilityToRole = (account_id, attachment_id, role_id) => {
  let result = axios()({
    method: "post",
    url: `${API.RESPONSIBILITY_V2}${account_id}/${API.RESPONSIBILITY_TO_POSITION}${attachment_id}/convert-to-role/`,
    data: { role_id },
  });
  return result;
};

// ER-11387 / Rule 1 — attach a Responsibility directly to a Position.
// POST .../responsibility-to-position/attach/  { position, responsibility }
// 201 body: { id, attachment_id, name, code, status, archived }
export const attachResponsibilityToPosition = (account_id, position_id, responsibility_id) => {
  return axios()({
    method: "post",
    url: `${API.RESPONSIBILITY_V2}${account_id}/${API.RESPONSIBILITY_TO_POSITION}attach/`,
    data: { position: position_id, responsibility: responsibility_id },
  });
};

// ER-11387 / Rule 12 — currently-attached direct rows for a Position.
// GET .../responsibility-to-position/attached/?position=<id>
// 200 body: { results: [{ id, attachment_id, name, code, status, archived }, ...] }
export const getDirectResponsibilitiesAttachedToPosition = (account_id, position_id) => {
  return axios()({
    method: "get",
    url: `${API.RESPONSIBILITY_V2}${account_id}/${API.RESPONSIBILITY_TO_POSITION}attached/`,
    params: { position: position_id },
  });
};

// POST .../responsibility-to-position/{attachment_id}/detach/
// Optional `data` body carries the detach reason. The Rule 18a dedupe path passes
// { reason: "SUPERSEDED_BY_ROLE", role_id, role_name } (Rule 2/3 semantics).
export const detachResponsibilityFromPosition = (account_id, attachment_id, data = {}) => {
  return axios()({
    method: "post",
    url: `${API.RESPONSIBILITY_V2}${account_id}/${API.RESPONSIBILITY_TO_POSITION}${attachment_id}/detach/`,
    data,
  });
};

export const fetchAttachableResponsibilitiesForPosition = (account_id, limit, offset, name) =>
  getListofResponsibilityV2(account_id, limit, offset, name, {
    status: ["active", "draft"],
    archived: false,
  });

export const getListofResponsibilityBadge = (account_id, res_id, type, params = {}) => {
  let url = `${API.RESPONSIBILITY_V2}${account_id}/${type}/${res_id}/badge/`;

  if (params._query) {
    url += `?${params._query}`;
    params = {};
  }

  let result = axios()({
    method: "get",
    url: url,
    params: params,
  });
  return result;
};

export const createResponsibilityBadge = (account_id, res_id, data) => {
  let result = axios()({
    method: "post",
    url: `${API.RESPONSIBILITY}${account_id}/responsibility/${res_id}/badge/`,
    data: data,
  });
  return result;
};

export const deleteResponsibilityBadge = (account_id, badge_id, data) => {
  let result = axios()({
    method: "post",
    url: `${API.RESPONSIBILITY_V2}${account_id}/badge/${badge_id}/remove/`,
    data: data,
  });
  return result;
};

export const getResponsibilityId = (account_id, res_id, type) => {
  let result = axios()({
    method: "get",
    url: `${API.RESPONSIBILITY}${account_id}/${type}/${res_id}/`,
  });
  return result;
};
export const getResponsibilityIdV2 = (account_id, res_id, type, params = {}) => {
  let result = axios()({
    method: "get",
    url: `${API.RESPONSIBILITY_V2}${account_id}/${type}/${res_id}/`,
    params,
  });
  return result;
};

export const getListofEmployeeOfResId = async (account_id, page_no, size, resId = null) => {
  const params = {
    page: page_no,
    size,
  };
  if (resId !== null && resId !== undefined) {
    params.exclude_employees_with_badge_for = resId;
  }
  const result = await axios()({
    method: "get",
    url: `${API.ACCOUNTList}${account_id}/employee/`,
    params,
  });

  return result;
};

export const getResponsibilityChatter = (account_id, res_id, page = 1) => {
  let result = axios()({
    method: "get",
    url: `${API.RESPONSIBILITY}${account_id}/responsibility/${res_id}/chatter/`,
    params: { page: page },
  });
  return result;
};
export const getResponsibilityChatterV2 = (
  account_id,
  res_id,
  page = 1,
  type = "responsibility"
) => {
  let result = axios()({
    method: "get",
    url: `${API.ACCOUNT_LIST_V2}${account_id}/chatter/?table=${type}&record_id=${res_id}`,
    params: { page: page },
  });
  return result;
};

export const getCheckpointsList = (account_id, res_id) => {
  let result = axios()({
    method: "get",
    url: `${API.RESPONSIBILITY}${account_id}/responsibility/${res_id}/checkpoint/`,
  });
  return result;
};

export const addCheckpoint = (account_id, res_id, data) => {
  let result = axios()({
    method: "post",
    url: `${API.RESPONSIBILITY}${account_id}/responsibility/${res_id}/checkpoint/`,
    data,
  });
  return result;
};

export const getResponsibilityChecklistV2 = (account_id, res_id, params = {}) => {
  // Filter out undefined/null values and build clean params object
  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(([, value]) => value != null && value !== "")
  );

  return axios()({
    method: "get",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${res_id}/checkpoint/`,
    params: cleanParams,
  });
};

/** GET single checkpoint (full nested fields e.g. `proposed_article`) for edit drill-down. */
export const getResponsibilityCheckpointDetailV2 = (account_id, res_id, checkpoint_id) => {
  return axios()({
    method: "get",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${res_id}/checkpoint/${checkpoint_id}/`,
  });
};

export const createResponsibilityCheckpoint = (account_id, res_id, data) => {
  let result = axios()({
    method: "post",
    url: `${API.RESPONSIBILITY}${account_id}/responsibility/${res_id}/checkpoint/create/`,
    data: data,
  });
  return result;
};

export const createResponsibilityCheckpointV2 = (account_id, res_id, data) => {
  let result = axios()({
    method: "post",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${res_id}/checkpoint/`,
    data: data,
  });
  return result;
};

export const uploadMicrolearningCheckPoint = (account_id, checkpoint_id, formData, signal) => {
  return axios().request({
    method: "post",
    url: `${API.RESPONSIBILITY_V2}${account_id}/checkpoint/${checkpoint_id}/video/`,
    data: formData,
    headers: {
      "Content-Type": "multipart/form-data",
    },
    signal,
  });
};

export const deleteMicrolearningCheckPoint = (account_id, checkpoint_id, video_id) => {
  return axios().request({
    method: "delete",
    url: `${API.RESPONSIBILITY}${account_id}/checkpoint/${checkpoint_id}/video/${video_id}`,
  });
};

export const deleteMicrolearningCheckPointV2 = (account_id, checkpoint_id, video_id) => {
  return axios().request({
    method: "delete",
    url: `${API.RESPONSIBILITY_V2}${account_id}/checkpoint/${checkpoint_id}/video/${video_id}/`,
  });
};

export const deleteProposedVideo = (account_id, checkpoint_id) => {
  return axios().request({
    method: "delete",
    url: `${API.RESPONSIBILITY_V2}${account_id}/checkpoint/${checkpoint_id}/proposed-video/`,
  });
};

export const proposeVideoInspectionDraft = (account_id, responsibility_id, checkpoint_id, data) => {
  return axios().request({
    method: "post",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${responsibility_id}/checkpoint/${checkpoint_id}/propose-video-inspection-draft/`,
    data,
  });
};

/**
 * POST — backend `Checkpoint.create_article_microlearning()` / `create-article-microlearning/`.
 * Run when user clicks Save and `proposed_article` is still null (first server bootstrap).
 */
export const createArticleMicrolearning = (account_id, responsibility_id, checkpoint_id) => {
  return axios().request({
    method: "post",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${responsibility_id}/checkpoint/${checkpoint_id}/create-article-microlearning/`,
  });
};

/**
 * POST — backend `Checkpoint.save_article(article)` / `save-art/`.
 * Persists `{ article: { title, body } }` (TipTap JSON doc in `body`).
 */
export const saveArticleMicrolearning = (account_id, responsibility_id, checkpoint_id, data) => {
  return axios().request({
    method: "post",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${responsibility_id}/checkpoint/${checkpoint_id}/save-article/`,
    data,
  });
};

/**
 * POST — backend Scribe import endpoint (ER-11751, "Scribe Import — Fetch, Parse,
 * and TipTap Content Mapping"). Fetches a public Scribe share link, parses it, and
 * maps its steps to TipTap content. Success returns the parsed article; 4xx raises
 * error codes SCRIBE_IMPORT_INVALID_URL / NOT_FOUND / PRIVATE_LINK / PARSE_FAILED.
 * Endpoint path is provisional until ER-11751 lands — reconcile if the service uses
 * a different suffix.
 */
export const importScribeArticle = (account_id, responsibility_id, checkpoint_id, data) => {
  return axios()({
    method: "post",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${responsibility_id}/checkpoint/${checkpoint_id}/${API.SCRIBE_IMPORT}`,
    data,
  });
};

/**
 * GET — poll a Scribe import background job (ER-12323 / ER-12335).
 * Returns `{ status: "pending" | "running" | "succeeded" | "failed", progress: { done, total }, ... }`.
 */
export const getScribeImportJobStatus = (account_id, responsibility_id, checkpoint_id, job_id) => {
  return axios()({
    method: "get",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${responsibility_id}/checkpoint/${checkpoint_id}/${API.SCRIBE_IMPORT}${job_id}/`,
  });
};

/**
 * POST — start a PDF import background job (ER-12393 / ER-12394). The dialog hands over one
 * PDF as `file`; the endpoint reads it from `pdf_file`, so it is re-keyed here. Resolves 202
 * `{ job_id }`, rejects 422 with PDF_IMPORT_INVALID_FILE / ENCRYPTED / FILE_TOO_LARGE. v2 only.
 */
export const importPdfArticle = (account_id, responsibility_id, checkpoint_id, formData) => {
  const payload = new FormData();
  formData.forEach((value, key) => {
    payload.append(key === "file" ? "pdf_file" : key, value);
  });
  return axios()({
    method: "post",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${responsibility_id}/checkpoint/${checkpoint_id}/${API.PDF_IMPORT}`,
    data: payload,
    headers: { "Content-Type": "multipart/form-data" },
  });
};

/**
 * GET — poll a PDF import background job (ER-12393 / ER-12394).
 * Returns `{ status: "queued" | "running" | "succeeded" | "failed", progress: { done, total }, result, error }`.
 */
export const getPdfImportJobStatus = (account_id, responsibility_id, checkpoint_id, job_id) => {
  return axios()({
    method: "get",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${responsibility_id}/checkpoint/${checkpoint_id}/${API.PDF_IMPORT}${job_id}/`,
  });
};

/**
 * POST — start a Word import background job (ER-12400). The dialog hands over one `.docx` as
 * `file`; the endpoint reads it from `word_file`, so it is re-keyed here. Resolves 202
 * `{ job_id }`, rejects 422 with a WORD_IMPORT_* code. v2 only.
 */
export const importWordArticle = (account_id, responsibility_id, checkpoint_id, formData) => {
  const payload = new FormData();
  formData.forEach((value, key) => {
    payload.append(key === "file" ? "word_file" : key, value);
  });
  return axios()({
    method: "post",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${responsibility_id}/checkpoint/${checkpoint_id}/${API.WORD_IMPORT}`,
    data: payload,
    headers: { "Content-Type": "multipart/form-data" },
  });
};

/**
 * GET — poll a Word import background job (ER-12400).
 * Returns `{ status: "queued" | "running" | "succeeded" | "failed", progress: { done, total }, result, error }`.
 */
export const getWordImportJobStatus = (account_id, responsibility_id, checkpoint_id, job_id) => {
  return axios()({
    method: "get",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${responsibility_id}/checkpoint/${checkpoint_id}/${API.WORD_IMPORT}${job_id}/`,
  });
};

export const deleteResponsibilityCheckpoint = (account_id, res_id, checkpoint_id) => {
  let result = axios()({
    method: "delete",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${res_id}/checkpoint/${checkpoint_id}/`,
  });
  return result;
};
export const checkpointStateUpdate = (account_id, res_id, checkpoint_id, type) => {
  let result = axios()({
    method: "post",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${res_id}/checkpoint/${checkpoint_id}/${type}/`,
  });
  return result;
};
export const addCheckpointQuestion = (account_id, responsibility_id, checkpoint_id, data) => {
  return axios()({
    method: "post",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${responsibility_id}/checkpoint/${checkpoint_id}/add-question/`,
    data,
  });
};

/** Body same shape as add-question: `{ question: { q_text, q_answers: [...] } }` — URL uses `q_no`. */
export const changeCheckpointQuestion = (
  account_id,
  responsibility_id,
  checkpoint_id,
  q_no,
  data
) => {
  return axios()({
    method: "patch",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${responsibility_id}/checkpoint/${checkpoint_id}/change-question/${q_no}/`,
    data,
  });
};

/** Atomic all-or-nothing replace. Body: `{ questions: [{ q_text, q_answers: [...] }] }`. */
export const replaceCheckpointQuestions = (account_id, responsibility_id, checkpoint_id, data) => {
  return axios()({
    method: "post",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${responsibility_id}/checkpoint/${checkpoint_id}/replace-questions/`,
    data,
  });
};

export const deleteCheckpointQuestion = (account_id, responsibility_id, checkpoint_id, q_no) => {
  return axios()({
    method: "delete",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${responsibility_id}/checkpoint/${checkpoint_id}/delete-question/${q_no}/`,
  });
};

/** Body: `{ step: { competence_category, inspector_action, look_for, pass_criteria, fail_criteria } }` */
export const addCheckpointInspectionStep = (account_id, responsibility_id, checkpoint_id, data) => {
  return axios()({
    method: "post",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${responsibility_id}/checkpoint/${checkpoint_id}/add-inspection-step/`,
    data,
  });
};

/** `step_id` in the path is the step sequence. Same body shape as add-inspection-step. */
export const changeCheckpointInspectionStep = (
  account_id,
  responsibility_id,
  checkpoint_id,
  step_id,
  data
) => {
  return axios()({
    method: "patch",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${responsibility_id}/checkpoint/${checkpoint_id}/change-inspection-step/${step_id}/`,
    data,
  });
};

/** `step_id` in the path is the step sequence. */
export const deleteCheckpointInspectionStep = (
  account_id,
  responsibility_id,
  checkpoint_id,
  step_id
) => {
  return axios()({
    method: "delete",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${responsibility_id}/checkpoint/${checkpoint_id}/delete-inspection-step/${step_id}/`,
  });
};

export const deleteCheckpointProtocol = (account_id, responsibility_id, checkpoint_id) => {
  return axios()({
    method: "post",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${responsibility_id}/checkpoint/${checkpoint_id}/delete-protocol/`,
  });
};

export const changeCheckpointV2 = (account_id, res_id, checkpoint_id, data) => {
  let result = axios()({
    method: "patch",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${res_id}/checkpoint/${checkpoint_id}/`,
    data: data,
  });
  return result;
};

/** Body: `{ quiz_is_autopass: boolean }` */
export const toggleCheckpointQuizAutopass = (
  account_id,
  responsibility_id,
  checkpoint_id,
  quiz_is_autopass
) => {
  return axios()({
    method: "post",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${responsibility_id}/checkpoint/${checkpoint_id}/toggle-quiz-autopass/`,
    data: { quiz_is_autopass: !!quiz_is_autopass },
  });
};

export const saveVideoInspectionInstructions = (
  account_id,
  responsibility_id,
  checkpoint_id,
  data
) => {
  return axios()({
    method: "post",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${responsibility_id}/checkpoint/${checkpoint_id}/save-video-inspection-instructions/`,
    data: data,
  });
};

export const discardProposedInspectionKit = (account_id, responsibility_id, checkpoint_id) => {
  return axios()({
    method: "post",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${responsibility_id}/checkpoint/${checkpoint_id}/discard-proposed-inspection-kit/`,
  });
};

/** Copy active microlearning into proposed_* (Propose Changes → copy active). */
export const proposeChangeStartingFromCopy = (account_id, responsibility_id, checkpoint_id) => {
  return axios()({
    method: "post",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${responsibility_id}/checkpoint/${checkpoint_id}/propose-change-starting-from-copy/`,
  });
};

/** Clear all proposed microlearning fields on an active checkpoint. */
export const discardChangeProposal = (account_id, responsibility_id, checkpoint_id) => {
  return axios()({
    method: "post",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${responsibility_id}/checkpoint/${checkpoint_id}/discard-change-proposal/`,
  });
};

/**
 * Assign a video upload task to an employee for a checkpoint.
 * Body: { assignee_id, deadline: "YYYY-MM-DD", notify: boolean }
 */
export const assignVideoUploadTask = (account_id, responsibility_id, checkpoint_id, data) => {
  return axios()({
    method: "post",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${responsibility_id}/checkpoint/${checkpoint_id}/assign-video-upload-task/`,
    data,
  });
};

/**
 * Cancel a pending video-upload task on a draft checkpoint (ER-10197).
 * Requires the edit-lock holder. Keeps the checkpoint row; reverts to inspector-only.
 * No request body. Success: updated checkpoint serializer.
 */
export const cancelUploadTask = (account_id, responsibility_id, checkpoint_id) => {
  return axios()({
    method: "post",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${responsibility_id}/checkpoint/${checkpoint_id}/cancel-upload-task/`,
  });
};

/**
 * Reassign an existing video-upload task (ER-10198).
 * Body: { assignee_id, deadline?: "YYYY-MM-DD"|null, notify?: boolean }
 * Omit `deadline` to leave unchanged; null/"" clears it.
 * Success: { id: <same task id> }
 */
export const reassignVideoUploadTask = (account_id, responsibility_id, checkpoint_id, data) => {
  return axios()({
    method: "post",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${responsibility_id}/checkpoint/${checkpoint_id}/reassign-video-upload-task/`,
    data,
  });
};

/**
 * Send a manual reminder for a pending video-upload task (ER-10199).
 */
export const sendUploadTaskReminder = (account_id, responsibility_id, checkpoint_id) => {
  return axios()({
    method: "post",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${responsibility_id}/checkpoint/${checkpoint_id}/send-reminder/`,
  });
};
export const publishProposedInspectionKit = (account_id, responsibility_id, checkpoint_id) => {
  return axios()({
    method: "post",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${responsibility_id}/checkpoint/${checkpoint_id}/publish-proposed-inspection-kit/`,
  });
};

export const removeActiveInspectionKit = (account_id, responsibility_id, checkpoint_id) => {
  return axios()({
    method: "post",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${responsibility_id}/checkpoint/${checkpoint_id}/remove-active-inspection-kit/`,
  });
};

export const removeCheckpointMicrolearning = (account_id, responsibility_id, checkpoint_id) => {
  return axios()({
    method: "post",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${responsibility_id}/checkpoint/${checkpoint_id}/remove-microlearning/`,
  });
};

export const changeCheckpoint = (account_id, res_id, checkpoint_id, data) => {
  let result = axios()({
    method: "patch",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${res_id}/checkpoint/${checkpoint_id}/change/`,
    data: data,
  });
  return result;
};

export const saveInstructionAsDraft = (account_id, res_id, checkpoint_id, data) => {
  let result = axios()({
    method: "post",
    url: `${API.PROFICIENCY_V1}${account_id}/responsibility/${res_id}/checkpoint/${checkpoint_id}/save-instruction-as-draft/`,
    data: data,
  });
  return result;
};

export const onUpdateCheckpointOrder = (account_id, res_id, data) => {
  let result = axios()({
    method: "patch",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${res_id}/reorder-checkpoint`,
    data: data,
  });
  return result;
};

/**
 * The evaluation endpoints expose the checkpoint outcome as `inspection_status`
 * on newer records, while older consumers read `status` (and some read
 * `evaluation_status`). Backfill `status` from those fields when it is empty so
 * every consumer gets a consistent outcome instead of a misleading "Pending".
 * Records that already carry `status` are returned untouched.
 */
export const backfillEvaluationOutcome = (record) => {
  if (!record || typeof record !== "object" || record.status) return record;
  const outcome = record.inspection_status || record.evaluation_status;
  return outcome ? { ...record, status: outcome } : record;
};

const normalizeEvaluationResponse = (result) => {
  const payload = result?.data;
  if (Array.isArray(payload)) {
    result.data = payload.map(backfillEvaluationOutcome);
  } else if (payload && Array.isArray(payload.results)) {
    result.data = {
      ...payload,
      results: payload.results.map(backfillEvaluationOutcome),
    };
  }
  return result;
};

export const getListActiveCheckpointsEval = async (account_id, responsibility_id, employee_id) => {
  let result = await axios()({
    method: "get",
    url: `${API.RESPONSIBILITY}${account_id}/responsibility/${responsibility_id}/employee/${employee_id}/`,
  });
  return normalizeEvaluationResponse(result);
};

export const getEmployeeChecklist = async (
  account_id,
  responsibility_id,
  employee_id,
  queryParams = {}
) => {
  let result = await axios()({
    method: "get",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${responsibility_id}/employee/${employee_id}/checklist/`,
    params: queryParams,
  });
  return result;
};

export const getEmployeeCheckpointDetail = async (account_id, responsibility_id, checkpoint_id) => {
  return axios()({
    method: "get",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${responsibility_id}/checkpoint/${checkpoint_id}/employee/`,
  });
};

export const postCheckpointInspectionEvent = async (
  account_id,
  responsibility_id,
  employee_id,
  checkpoint_id,
  data
) => {
  return axios()({
    method: "post",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${responsibility_id}/employee/${employee_id}/checkpoint/${checkpoint_id}/inspection-event/`,
    data,
  });
};

export const postCheckpointLearningEvent = async (
  account_id,
  responsibility_id,
  employee_id,
  checkpoint_id,
  data
) => {
  return axios()({
    method: "post",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${responsibility_id}/employee/${employee_id}/checkpoint/${checkpoint_id}/learning-event/`,
    data,
  });
};

export const submitCheckpointQuizAttempt = async (
  account_id,
  responsibility_id,
  checkpoint_id,
  data
) => {
  return axios()({
    method: "post",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${responsibility_id}/checkpoint/${checkpoint_id}/submit-quiz-attempt/`,
    data,
  });
};
export const getListActiveCheckpointsEvalV2 = async (
  account_id,
  responsibility,
  employee,
  limit,
  offset
) => {
  const params = {
    responsibility: responsibility,
    employee: employee,
    limit: limit,
    offset: offset,
  };
  let result = await axios()({
    method: "get",
    url: `${API.RESPONSIBILITY_V2}${account_id}/evaluation/`,
    params: params,
  });
  return normalizeEvaluationResponse(result);
};
export const getAssignmentEval = async (account_id, assignment_id, object_type) => {
  let result = await axios()({
    method: "get",
    url: `${API.ACCOUNTList}${account_id}/assignment/${object_type}/${assignment_id}/evaluate/`,
  });
  return result;
};

export const getCheckpointChatterList = (account_id, checkpoint_id, page = 1) => {
  let result = axios()({
    method: "get",
    url: `${API.RESPONSIBILITY}${account_id}/checkpoint/${checkpoint_id}/chatter/`,
    params: { page: page },
  });
  return result;
};

export const getResponsibilityAssignments = (account_id, responsibility_id) => {
  let result = axios()({
    method: "get",
    url: `${API.RESPONSIBILITY}${account_id}/responsibility/${responsibility_id}/assignments/`,
  });
  return result;
};
export const getResponsibilityAssignmentsV2 = (account_id, responsibility_id) => {
  let result = axios()({
    method: "get",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${responsibility_id}/assignments/`,
  });
  return result;
};

export const createProficiencyBadge = (account_id, payload) => {
  let result = axios()({
    method: "post",
    url: `${API.RESPONSIBILITY_V2}${account_id}/create-proficiency-badge/`,
    data: payload,
  });
  return result;
};

export const deleteBadge = (account_id, badge_id) => {
  let result = axios()({
    method: "delete",
    url: `${API.RESPONSIBILITY_V2}${account_id}/badge/${badge_id}/`,
  });
  return result;
};

export const updateBadge = (account_id, badge_id, data) => {
  let result = axios()({
    method: "patch",
    url: `${API.RESPONSIBILITY_V2}${account_id}/badge/${badge_id}/`,
    data: data,
  });
  return result;
};

export const moveCheckpointOnResponsibility = (account_id, checkpoint_id, data) => {
  let result = axios()({
    method: "post",
    url: `${API.RESPONSIBILITY_V2}${account_id}/checkpoint/${checkpoint_id}/move/`,
    data: data,
  });
  return result;
};

export const reOrderSequenceData = (account_id, role_id, data) => {
  let result = axios()({
    method: "post",
    url: `${API.ACCOUNTList}${account_id}/role/${role_id}/sequence-reorder/`,
    data: { reordered_ids: data },
  });
  return result;
};

export const createBulkCheckpoints = (account_id, responsibility_id, data) => {
  let result = axios()({
    method: "post",
    url: `${API.PROFICIENCY_V1}${account_id}/responsibility/${responsibility_id}/checkpoint/bulk/`,
    data: data,
  });
  return result;
};

export const bulkActivateCheckpoints = (account_id, responsibility_id, checkpoint_ids) => {
  let result = axios()({
    method: "post",
    url: `${API.PROFICIENCY_V2}${account_id}/responsibility/${responsibility_id}/checkpoint/bulk-activate/`,
    data: {
      checkpoint_ids: checkpoint_ids,
    },
  });
  return result;
};

export const bulkDeleteCheckpoints = (account_id, responsibility_id, checkpoint_ids) => {
  let result = axios()({
    method: "delete",
    url: `${API.PROFICIENCY_V2}${account_id}/responsibility/${responsibility_id}/checkpoint/delete-bulk/`,
    data: {
      checkpoint_ids: checkpoint_ids,
    },
  });
  return result;
};

export const bulkArchiveCheckpoints = (account_id, responsibility_id, checkpoint_ids) => {
  let result = axios()({
    method: "post",
    url: `${API.PROFICIENCY_V2}${account_id}/responsibility/${responsibility_id}/checkpoint/bulk-archive/`,
    data: {
      checkpoint_ids: checkpoint_ids,
    },
  });
  return result;
};

export const bulkRetireCheckpoints = (account_id, responsibility_id, checkpoint_ids) => {
  let result = axios()({
    method: "post",
    url: `${API.PROFICIENCY_V2}${account_id}/responsibility/${responsibility_id}/checkpoint/bulk-retire/`,
    data: {
      checkpoint_ids: checkpoint_ids,
    },
  });
  return result;
};

export const bulkMoveCheckpoints = (account_id, checkpoint_ids, target_responsibility_id) => {
  let result = axios()({
    method: "post",
    url: `${API.PROFICIENCY_V2}${account_id}/checkpoint/bulk-move/`,
    data: {
      checkpoint_ids: checkpoint_ids,
      target_responsibility_id: target_responsibility_id,
    },
  });
  return result;
};

export const createBulkResponsiblities = (account_id, data) => {
  let result = axios()({
    method: "post",
    url: `${API.PROFICIENCY_V1}${account_id}/responsibility/bulk/`,
    data: data,
  });
  return result;
};

export const getResponsibilityAssignment = (
  account_id,
  role_assignment_id,
  status = null,
  checkpoint_status = null
) => {
  let params = { role_assignment: role_assignment_id };
  if (status) {
    params.status = status;
  }
  if (checkpoint_status) {
    params.checkpoint_status = checkpoint_status;
  }

  let result = axios()({
    method: "get",
    url: `${API.PROFICIENCY_V2}${account_id}/responsibility-assignment/`,
    params: params,
  });
  return result;
};

// Direct-attached (Position-level) Responsibility Assignments — ER-11412 / ER-11413.
// The responsibility-assignment v2 endpoint accepts a `position_assignment` filter
// for assignments whose parent is a PositionAssignment (role_assignment is null).
// Mirrors getRoleAssignment's filter shape (employee + position_assignment + status).
export const getDirectResponsibilityAssignment = (
  account_id,
  employee_id,
  position_assignment_id,
  status = null
) => {
  let params = {
    employee: employee_id,
    position_assignment: position_assignment_id,
  };
  if (status) {
    params.status = status;
  }

  let result = axios()({
    method: "get",
    url: `${API.PROFICIENCY_V2}${account_id}/responsibility-assignment/`,
    params: params,
  });
  return result;
};

export const createResponsibilityDefaultBadge = (account_id, data) => {
  let result = axios()({
    method: "post",
    url: `${API.RESPONSIBILITY}${account_id}/badge/`,
    data: data,
  });
  return result;
};
export const fetchAttachableResponsibilities = (account_id, role_id, limit, offset, name) => {
  let result = axios()({
    method: "get",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/`,
    params: {
      limit,
      offset,
      name,
      attachable_to_role: role_id,
    },
  });
  return result;
};
export const fetchAttachableRoles = (account_id, responsibility_id, limit, offset, name) =>
  requestV2RoleList(account_id, {
    limit,
    offset,
    name,
    attachable_to_responsibility: responsibility_id,
  });

export const fetchAttachableRolesForPosition = (account_id, position_id, limit, offset, name) =>
  requestV2RoleList(account_id, {
    limit,
    offset,
    name,
    attachable_to: position_id,
  });
export const updateResponsibilityDefaults = (account_id, responsibility_id, data) => {
  let result = axios()({
    method: "patch",
    url: `${API.PROFICIENCY_V2}${account_id}/responsibility/${responsibility_id}/`,
    data: data,
  });
  return result;
};
export const updateArchitectWillCoach = (account_id, responsibility_id, architect_will_coach) => {
  return axios()({
    method: "patch",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${responsibility_id}/`,
    data: { architect_will_coach },
  });
};

export const importResponsibility = (account_id, formData) => {
  return axios()({
    method: "post",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/import/`,
    data: formData,
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

/**
 * GET — signed Cloudflare Stream playback URL for a checkpoint microlearning video.
 */
export const getCheckpointVideoSignedUrl = (account_id, responsibility_id, checkpoint_id) => {
  return axios()({
    method: "get",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/${responsibility_id}/checkpoint/${checkpoint_id}/video-signed-url/`,
  });
};

/** Relative path for checkpoint presence SSE (Token auth via fetch streaming). */
export const getCheckpointPresenceSsePath = (account_id, checkpoint_id) =>
  `${API.RESPONSIBILITY_V2}${account_id}/checkpoint/${checkpoint_id}/presence/`;

/**
 * POST — refresh presence TTL (+ optional viewing_metadata) while viewing a checkpoint.
 * Does not require holding the edit lock.
 */
export const heartbeatCheckpointPresence = (account_id, checkpoint_id, data = {}) => {
  return axios()({
    method: "post",
    url: `${API.RESPONSIBILITY_V2}${account_id}/checkpoint/${checkpoint_id}/presence/heartbeat/`,
    data,
  });
};

/** POST — acquire exclusive edit lock (Rule 11). */
export const acquireCheckpointEditLock = (account_id, checkpoint_id) => {
  return axios()({
    method: "post",
    url: `${API.RESPONSIBILITY_V2}${account_id}/checkpoint/${checkpoint_id}/acquire-edit-lock/`,
  });
};

/**
 * POST — refresh edit-lock heartbeat (~every 10–15s while editing).
 * Also refreshes presence TTL as a side effect.
 */
export const heartbeatCheckpointEditLock = (account_id, checkpoint_id) => {
  return axios()({
    method: "post",
    url: `${API.RESPONSIBILITY_V2}${account_id}/checkpoint/${checkpoint_id}/heartbeat-edit-lock/`,
  });
};

/**
 * POST — release edit lock.
 * @param {string|number} account_id
 * @param {string|number} checkpoint_id
 * @param {{ reason?: string }} [data] — `explicit` | `saved` | `activation` (holder-gated)
 */
export const releaseCheckpointEditLock = (
  account_id,
  checkpoint_id,
  data = { reason: "explicit" }
) => {
  return axios()({
    method: "post",
    url: `${API.RESPONSIBILITY_V2}${account_id}/checkpoint/${checkpoint_id}/release-edit-lock/`,
    data,
  });
};

/** POST — take over a stale edit lock (≥15s since last lock heartbeat). */
export const takeOverCheckpointEditLock = (account_id, checkpoint_id) => {
  return axios()({
    method: "post",
    url: `${API.RESPONSIBILITY_V2}${account_id}/checkpoint/${checkpoint_id}/take-over-edit-lock/`,
  });
};

/**
 * POST — publish `checkpoint_updated` on the presence SSE channel after a successful save.
 * Does not persist checkpoint content; call only after the update API succeeds.
 */
export const notifyCheckpointPresenceUpdated = (account_id, checkpoint_id) => {
  return axios()({
    method: "post",
    url: `${API.RESPONSIBILITY_V2}${account_id}/checkpoint/${checkpoint_id}/presence/checkpoint-updated/`,
    data: {},
  });
};

/**
 * GET — CheckpointVersion history for a checkpoint (live + superseded), newest first.
 * Paginated: `{ count, next, previous, results }`.
 */
export const getCheckpointVersions = (account_id, checkpoint_id, params = {}) => {
  return axios()({
    method: "get",
    url: `${API.RESPONSIBILITY_V2}${account_id}/checkpoint/${checkpoint_id}/versions/`,
    params,
  });
};

/**
 * Create a DRAFT Responsibility and attach it to a Role or Position in one atomic operation
 * (ER-12095 / MDP-617, Responsibility Rule 5.1).
 * Gated by idea-MDP-617 flag (404 when off).
 * @param {number|string} account_id
 * @param {object} data - { name, target_type, target_id, context? }
 * @returns {Promise} Axios response with created Responsibility
 */
export const createDraftAndAttach = (account_id, data) => {
  return axios()({
    method: "post",
    url: `${API.RESPONSIBILITY_V2}${account_id}/responsibility/create-draft-and-attach/`,
    data,
  });
};
